<script lang="ts">
	import type { SearchModule, Module, SmartSearch, SearchFieldProps } from 'utility/search-fields'
	import type { i18n } from 'types/common'

	import { onMount, type ComponentProps } from 'svelte'
	import Pin from 'components/search/Pin.svelte'
	import Input from '@isoftdata/svelte-input'
	import Modal from '@isoftdata/svelte-modal'
	import Button from '@isoftdata/svelte-button'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import Popover from '@isoftdata/svelte-popover'
	import { getContext } from 'svelte'

	interface Props
		extends
			Omit<ComponentProps<typeof Input<string>>, 'value' | 'onchange' | 'placeholder'>,
			SearchFieldProps<SmartSearch> {
		initialValue?: SmartSearch
		keyword?: string
		showModuleModal?: boolean
		searchModules?: Array<SearchModule>
		selectedSearchModuleIds: Array<Module['id']> //Required
	}

	let {
		inEditMode,
		keyword = $bindable(''),
		searchField,
		disabled = false,
		showModuleModal = $bindable(false),
		searchModules = [],
		pinnedSearchFields,
		selectedSearchModuleIds = $bindable(),
		selectedSearchFieldsKeys,
		initialValue = undefined,
		change,
		...rest
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	function toggleSearchModuleSelection(toggledSearchModuleId: Module['id']) {
		if (!selectedSearchModuleIds.includes(toggledSearchModuleId)) {
			//Add
			selectedSearchModuleIds = [...selectedSearchModuleIds, toggledSearchModuleId]
		} else {
			//Remove
			selectedSearchModuleIds = selectedSearchModuleIds.filter(moduleId => moduleId !== toggledSearchModuleId)
		}
		change?.({
			field: searchField,
			value: {
				keyword,
				moduleIds: selectedSearchModuleIds,
			},
		})
	}

	const disableInput = $derived(disabled || inEditMode)

	const selectedSearchModulesMap = $derived(
		new Map(
			searchModules
				.filter(searchModule => selectedSearchModuleIds.includes(searchModule.module.id))
				.map(searchModule => [searchModule.module.id, searchModule]),
		),
	)

	onMount(() => {
		if (initialValue) {
			keyword = initialValue.keyword
			selectedSearchModuleIds = initialValue.moduleIds
		}
	})
</script>

<EditModeControls
	{searchField}
	{inEditMode}
	{pinnedSearchFields}
	{selectedSearchFieldsKeys}
>
	<Input
		{...rest}
		label={translate('partSearch.smartSearch', 'Smart Search')}
		disabled={disableInput}
		placeholder={translate('partSearch.searchUsingModules', 'Search using {{count}} modules', {
			count: selectedSearchModuleIds.length,
		})}
		bind:value={keyword}
		onchange={() => {
			change?.({ field: searchField, value: { keyword, moduleIds: selectedSearchModuleIds } })
		}}
	>
		{#snippet hint()}
			<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
		{/snippet}
		{#snippet append()}
			<Popover
				outline
				autoUpdate
				size="sm"
				color="secondary"
				icon="info-circle"
			>
				{selectedSearchModuleIds.length}
				{#snippet header()}
					Search Using:
				{/snippet}
				{#snippet body()}
					{#each selectedSearchModuleIds as moduleId}
						<span class="badge text-bg-primary me-1">{selectedSearchModulesMap.get(moduleId)?.module.name ?? ''}</span>
					{:else}
						{translate(
							'partSearch.noSearchModulesSelectedUseTheEditButtonToSelectSearchModules',
							'No search modules selected. Use the edit button to select search modules.',
						)}
					{/each}
				{/snippet}
			</Popover>
			<Button
				outline
				{disabled}
				iconClass="gear"
				onclick={() => {
					showModuleModal = true
					return
				}}
			/>
		{/snippet}
	</Input>

	<Modal
		cancelShown={false}
		title={translate('partSearch.selectSearchModules', 'Select Search Modules')}
		confirmButtonText={translate('common:done', 'Done')}
		bind:show={showModuleModal}
		confirm={() => {
			showModuleModal = false
		}}
		close={() => (showModuleModal = false)}
	>
		<div
			class="list-group"
			style="max-height: 60vh; overflow-y: auto;"
		>
			{#each searchModules as searchModule}
				<button
					class="list-group-item list-group-item-action py-2 px-3"
					class:active={selectedSearchModulesMap.has(searchModule.module.id)}
					onclick={() => toggleSearchModuleSelection(searchModule.module.id)}
				>
					{searchModule.module.name}
				</button>
			{/each}
		</div>

		{#snippet footer()}
			<Button
				outline
				icon="check-double"
				disabled={selectedSearchModuleIds.length === searchModules.length}
				onclick={() => {
					selectedSearchModuleIds = searchModules.map(searchModule => searchModule.module.id)
				}}>{translate('common:selectAll', 'Select All')}</Button
			>
			<Button
				outline
				icon={{
					icon: 'square',
					prefix: 'far',
				}}
				disabled={!selectedSearchModuleIds.length}
				onclick={() => {
					selectedSearchModuleIds = []
				}}>{translate('common:selectNone', 'Select None')}</Button
			>
		{/snippet}
	</Modal>
</EditModeControls>
